import type { ReactElement } from 'react';
import { formatPrice } from '@fshr/shared';
import BevSafeText from '../../../bev-safe-text';
import Button from '../../../button';

import getTicketCountsBasedOnType from './get-ticket-counts-based-on-type';
import type { EventProduct } from './types';

export type BookingConfirmationProps = {
	locale: string;
	eventProduct: EventProduct;
	isSubmitting: boolean;
	t: (text: string, params?: (string | number)[]) => string;
	onGoBack: () => void;
	onConfirm: () => void;
};

/** Ported from `ProductItemConfirmation.tsx`: the price-breakdown/confirm step shown between adding an
 * event to an already-booked itinerary and confirming it. */
export default function BookingConfirmation({
	locale,
	eventProduct,
	isSubmitting,
	t,
	onGoBack,
	onConfirm,
}: BookingConfirmationProps): ReactElement {
	const ticketTypes = eventProduct?.price?.ticketBreakdown?.ticketTypes || [];
	const totalPrice = eventProduct?.price?.totalPrice;
	const { numAdults, numChildren } = eventProduct || {};

	const titleFor = (ticket: (typeof ticketTypes)[number]) => {
		const { amount, currencyCode = '', ticketCount = 0, type = '' } = ticket;
		const counts = getTicketCountsBasedOnType(ticket, numAdults, numChildren);

		switch (type) {
			case 'GRP':
				return counts.adults && counts.children
					? t(
							`Base Rate - {0} ${counts.adults > 1 ? 'Adults' : 'Adult'}, {1} ${counts.children > 1 ? 'Children' : 'Child'}`,
							[counts.adults, counts.children]
						)
					: t(
							`Base Rate - {0} ${counts.adults ? (counts.adults > 1 ? 'Adults' : 'Adult') : counts.children && counts.children > 1 ? 'Children' : 'Child'}`,
							[counts.adults || counts.children || 0]
						);
			case 'AAT':
				return t(`{0} {1} x {2} Extra ${counts.adults && counts.adults > 1 ? 'Adults' : 'Adult'}`, [
					currencyCode,
					String(ticket.perTicketAmount),
					String(counts.adults),
				]);
			case 'ACD':
				return t(
					`{0} {1} x {2} Extra ${counts.children && counts.children > 1 ? 'Children' : 'Child'}`,
					[currencyCode, String(ticket.perTicketAmount), String(counts.children)]
				);
			case 'UNI':
				return t('Flat Rate');
			case 'ADT':
				return amount !== undefined && ticketCount
					? t(`{0} {1} x {2} ${ticketCount > 1 ? 'Adults' : 'Adult'}`, [
							currencyCode,
							`${amount / ticketCount}`,
							`${ticketCount}`,
						])
					: '';
			case 'CHD':
				return amount !== undefined && ticketCount
					? t(`{0} {1} x {2} ${ticketCount > 1 ? 'Children' : 'Child'}`, [
							currencyCode,
							`${amount / ticketCount}`,
							`${ticketCount}`,
						])
					: '';
			default:
				return '';
		}
	};

	return (
		<div className="flex flex-col gap-300" data-testid="booking-confirmation">
			<div className="flex flex-col gap-100">
				{ticketTypes.map(ticket => (
					<div key={ticket.type} className="flex items-center justify-between">
						<span className="text-body3 text-text-secondary">
							<BevSafeText>{titleFor(ticket)}</BevSafeText>
						</span>
						<span className="text-body2 text-text-heading">
							{ticket.amount === 0
								? t('Complimentary')
								: formatPrice({
										value: ticket.amount,
										currencyCode: ticket.currencyCode,
										decimalPlaces: 0,
										locale,
									})}
						</span>
					</div>
				))}
			</div>

			<div className="flex flex-col gap-100 border-t border-border-subtle pt-200">
				<div className="flex items-center justify-between">
					<span className="text-subtitle2 text-text-heading">{t('Est. Total*')}</span>
					<span className="text-subtitle1 text-text-heading">
						{formatPrice({
							value: totalPrice?.amount,
							currencyCode: totalPrice?.currencyCode,
							decimalPlaces: 0,
							locale,
						})}
					</span>
				</div>
				<p className="text-caption text-text-secondary">
					{t(
						'Additional taxes and services fees may apply. The total cost will be reflected on your invoice from the Hotel.'
					)}
				</p>
			</div>

			<div className="flex gap-200">
				<Button intent="outline" onClick={onGoBack} disabled={isSubmitting} className="flex-1">
					{t('Go Back')}
				</Button>
				<Button intent="filled" onClick={onConfirm} disabled={isSubmitting} className="flex-1">
					{t('Add To Itinerary')}
				</Button>
			</div>
		</div>
	);
}
